import { useMemo, useState } from "react";
import type { MovieItem } from "@/types/movie";
import { genreList, quickCategories } from "@/mocks/siteContent";
import MovieCard from "@/pages/home/components/MovieCard";

interface CatalogSectionProps {
  items: MovieItem[];
  searchTerm: string;
  activeCategory: string;
  activeGenre: string;
  onSearchTerm: (value: string) => void;
  onCategoryChange: (value: string) => void;
  onGenreChange: (value: string) => void;
}

const sortOptions = [
  { value: "default", label: "综合排序" },
  { value: "rating", label: "评分最高" },
  { value: "year", label: "最新上线" },
];

const genreOptions = ["全部类型", ...genreList.map((g) => g.name)];

export default function CatalogSection({
  items,
  searchTerm,
  activeCategory,
  activeGenre,
  onSearchTerm,
  onCategoryChange,
  onGenreChange,
}: CatalogSectionProps) {
  const [sortBy, setSortBy] = useState("default");

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const result = items.filter((item) => {
      const matchCategory = activeCategory === "全部" || item.category === activeCategory;
      const matchGenre = activeGenre === "全部类型" || item.genre === activeGenre;
      const matchTerm =
        !term ||
        item.title.toLowerCase().includes(term) ||
        item.genre.toLowerCase().includes(term) ||
        item.category.toLowerCase().includes(term) ||
        item.summary.toLowerCase().includes(term);
      return matchCategory && matchGenre && matchTerm;
    });

    if (sortBy === "rating") {
      return [...result].sort((a, b) => b.rating - a.rating);
    }
    if (sortBy === "year") {
      return [...result].sort((a, b) => b.year - a.year || b.rating - a.rating);
    }
    return result;
  }, [items, searchTerm, activeCategory, activeGenre, sortBy]);

  const hasFilter = activeCategory !== "全部" || activeGenre !== "全部类型" || searchTerm.trim() !== "";

  const resetFilters = () => {
    onCategoryChange("全部");
    onGenreChange("全部类型");
    onSearchTerm("");
    setSortBy("default");
  };

  return (
    <section id="catalog" className="scroll-mt-16 bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-8xl px-4 md:px-8">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="text-sm font-medium text-accent-700">影视资源库</span>
            <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              最新电影与热门电视剧，分类筛选一站直达
            </h2>
          </div>
          <p className="max-w-md text-sm leading-relaxed text-foreground-600">
            支持按分类、类型与评分排序浏览，也可以在下方直接输入片名或题材关键词，筛选仅作用于本页已收录的影视条目。
          </p>
        </div>

        <div className="mt-8 rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2">
              {quickCategories.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => onCategoryChange(cat)}
                  className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                    activeCategory === cat
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-50 text-foreground-700 hover:bg-secondary-100"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="relative">
                <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-500"></i>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => onSearchTerm(e.target.value)}
                  placeholder="搜索片名 / 题材"
                  aria-label="搜索影视库"
                  className="w-full rounded-md border border-background-200 bg-background-50 py-2 pl-9 pr-3 text-sm text-foreground-950 placeholder:text-foreground-500 focus:border-primary-400 focus:outline-none sm:w-44"
                />
              </div>
              <select
                value={activeGenre}
                onChange={(e) => onGenreChange(e.target.value)}
                aria-label="按类型筛选"
                className="cursor-pointer rounded-md border border-background-200 bg-background-50 px-3 py-2 text-sm text-foreground-950 focus:border-primary-400 focus:outline-none"
              >
                {genreOptions.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                aria-label="排序方式"
                className="cursor-pointer rounded-md border border-background-200 bg-background-50 px-3 py-2 text-sm text-foreground-950 focus:border-primary-400 focus:outline-none"
              >
                {sortOptions.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-background-200 pt-3">
            <p className="text-xs text-foreground-600">
              共找到 <span className="font-semibold text-accent-700">{filtered.length}</span> 部影视
              {activeCategory !== "全部" && ` · 分类：${activeCategory}`}
              {activeGenre !== "全部类型" && ` · 类型：${activeGenre}`}
              {searchTerm.trim() && ` · 关键词：${searchTerm.trim()}`}
            </p>
            {hasFilter && (
              <button
                type="button"
                onClick={resetFilters}
                className="flex items-center gap-1 whitespace-nowrap rounded-md px-2 py-1 text-xs font-medium text-foreground-600 transition-colors hover:bg-secondary-100 hover:text-foreground-950"
              >
                <i className="ri-refresh-line"></i>
                清除筛选
              </button>
            )}
          </div>
        </div>

        {filtered.length > 0 ? (
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4 xl:grid-cols-5">
            {filtered.map((item) => (
              <MovieCard key={item.id} item={item} />
            ))}
          </div>
        ) : (
          <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
            <i className="ri-search-eye-line text-3xl text-background-400"></i>
            <p className="mt-3 text-sm font-medium text-foreground-800">没有找到匹配的影视条目</p>
            <p className="mt-1 text-xs text-foreground-500">换个关键词，或点「清除筛选」查看全部资源</p>
            <button
              type="button"
              onClick={resetFilters}
              className="mt-5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              查看全部影视
            </button>
          </div>
        )}
      </div>
    </section>
  );
}